import React, { memo, ReactNode, useMemo } from 'react';

import { addRootNodeClassName, cn } from '../../lib/css/className/className';
import { TextProps } from '../TextGeneric/TextGeneric';
import { HStack, Stack } from '../Stack';
import { TextPresetWithSkin } from '../TextGeneric/types';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import { useInternalVerticalAlignWithSkin } from '../../hooks/useVerticalAlign/useVerticalAlignWithSkin';
import { TextGenericWithSkin } from '../TextGeneric/TextGenericWithSkin';

import './ElementHeader.css';

const cls = cn('ElementHeader');

export interface ElementHeaderProps extends Omit<TextProps, 'textSize' | 'bold' | 'role' | 'as'> {
    /**
     * Признак использования жирного начертания для подписи
     */
    isBold?: boolean;

    /**
     * Текстовый аддон справа от заголовка.
     * Использует отличное от `addonRight` выравнивание специально для текста.
     * Размер и цвет аддона задаётся в соответствии с размером заголовка и определён дизайн-системой.
     */
    textAddonRight?: ReactNode;

    /**
     * Размер текста
     * @default m
     */
    size?: 'm' | 's' | 'xs';

    /**
     * Бэйдж
     */
    badge?: ReactNode;

    /**
     * Признак горизонтального расположения элементов поля
     * @default false
     */
    horizontalLayout?: boolean;
}

/**
 * Получение размера текста в зависимости от наличия описания
 * @param caption - дополнительный поясняющий текст (описание)
 * @returns textSize
 */
function getTextSize(caption?: ReactNode): TextPresetWithSkin {
    if (caption) {
        return 'current-main + current-body';
    }

    return 'current-main';
}

/**
 * Компонент для создания подписей к полям
 * @internal - компонент для FormField
 */
export const ElementHeader = memo<ElementHeaderProps>(function ElementHeader({
    className,
    children,
    caption,
    isBold,
    color = 'gray',
    textAddonRight,
    addonRight,
    size = 'm',
    badge,
    horizontalLayout,
    ...props
}) {
    const sizeSkinClass = getSizeSkin(size);
    const textSize = getTextSize(caption);
    const { addonClasses } = useInternalVerticalAlignWithSkin({ textSize, addonType: 'icon' });

    const textAddonRightComponent = useMemo(() => {
        if (!textAddonRight) {
            return null;
        }

        return (
            <TextGenericWithSkin textSize={'current-body'} skinSize={size} color="gray" role="supplementary">
                {textAddonRight}
            </TextGenericWithSkin>
        );
    }, [textAddonRight, size]);

    const addonRightComponent = useMemo(() => {
        if (!addonRight) {
            return null;
        }

        return <Stack className={addonClasses}>{addonRight}</Stack>;
    }, [addonRight, addonClasses]);

    const addonRightContent = useMemo(() => {
        if (!(badge || textAddonRightComponent || addonRightComponent)) {
            return null;
        }

        return (
            <HStack gap="current-geometry" className={cls('addonContainer')} alignItems="center">
                {badge}
                {textAddonRightComponent}
                {addonRightComponent}
            </HStack>
        );
    }, [badge, textAddonRightComponent, addonRightComponent]);

    let content = (
        <TextGenericWithSkin
            {...props}
            className={cls('header')}
            labelClassName={cls('label')}
            textSize={textSize}
            skinSize={size}
            caption={caption}
            color={color}
            bold={isBold}
            addonRight={addonRightContent}
        >
            {children}
        </TextGenericWithSkin>
    );

    return addRootNodeClassName(
        content,
        cls({ withCaption: Boolean(caption), horizontal: horizontalLayout, size }, [sizeSkinClass, className]),
    );
});
